import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ImageSquare, Upload, Trash, ToggleLeft, ToggleRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

export default function VoucherImagesPage() {
  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [brand, setBrand] = useState("default");
  const [label, setLabel] = useState("");

  const fetchImages = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await axios.get(`${API}/voucher-images`);
      setImages(res.data.images);
    } catch (error) {
      toast.error("Failed to load images");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchImages(); }, [fetchImages]);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    let uploaded = 0;
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("brand", brand);
        formData.append("label", label || file.name);
        await axios.post(`${API}/voucher-images/upload`, formData, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded++;
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    if (uploaded > 0) {
      toast.success(`${uploaded} image(s) uploaded`);
      fetchImages();
    }
    setUploading(false);
    e.target.value = "";
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm("Delete this image?")) return;
    try {
      await axios.delete(`${API}/voucher-images/${imageId}`);
      toast.success("Image deleted");
      fetchImages();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const handleToggle = async (imageId) => {
    try {
      await axios.put(`${API}/voucher-images/${imageId}/toggle`);
      toast.success("Status updated");
      fetchImages();
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  return (
    <div className="space-y-6" data-testid="voucher-images-page">
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-[#0A0A0A]">Voucher Images</h2>
          <p className="text-[#64748B] text-sm mt-1">Upload and manage voucher images sent to customers via WhatsApp</p>
        </div>
      </div>

      {/* Upload Card */}
      <Card className="border border-[#E2E8F0]">
        <CardHeader className="pb-4">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Upload size={22} className="text-[#002FA7]" />Upload Voucher Images
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-[#64748B]">Brand</Label>
              <Input placeholder="e.g., Dominos or default" value={brand} onChange={(e) => setBrand(e.target.value)} data-testid="image-brand-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-[#64748B]">Label</Label>
              <Input placeholder="e.g., Welcome Voucher" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="image-label-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-[#64748B]">Image File(s)</Label>
              <div className="relative">
                <input type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" id="image-upload" data-testid="image-file-input" />
                <Button asChild variant="outline" className="w-full gap-2" disabled={uploading}>
                  <label htmlFor="image-upload" className="cursor-pointer">
                    <Upload size={18} />
                    {uploading ? "Uploading..." : "Choose Images"}
                  </label>
                </Button>
              </div>
            </div>
          </div>
          <p className="text-xs text-[#94A3B8] mt-3">Active images are sent to customers when they receive vouchers via WhatsApp. Set brand to "default" for generic voucher images.</p>
        </CardContent>
      </Card>

      {/* Images Table */}
      <Card className="border border-[#E2E8F0]">
        <CardHeader className="border-b border-[#E2E8F0] pb-4">
          <CardTitle className="font-heading text-lg flex items-center justify-between">
            <span className="flex items-center gap-2"><ImageSquare size={24} className="text-[#002FA7]" />Uploaded Images</span>
            <span className="text-sm font-normal text-[#64748B]">{images.length} images</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? <div className="p-8 text-center text-[#64748B]">Loading...</div> : images.length === 0 ? <div className="p-8 text-center text-[#64748B]">No voucher images uploaded yet.</div> : (
            <div className="overflow-x-auto">
              <Table className="data-table">
                <TableHeader>
                  <TableRow className="bg-[#F8FAFC]">
                    <TableHead>Preview</TableHead>
                    <TableHead>Label</TableHead>
                    <TableHead>Brand</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Uploaded</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {images.map((img) => (
                    <TableRow key={img.id} className="table-row-hover" data-testid={`image-row-${img.id}`}>
                      <TableCell>
                        <img src={`${BASE_URL}${img.image_url}`} alt={img.label} className="h-12 w-12 object-cover rounded border" />
                      </TableCell>
                      <TableCell className="font-medium">{img.label}</TableCell>
                      <TableCell><Badge className="bg-[#FEF3C7] text-[#92400E]">{img.brand}</Badge></TableCell>
                      <TableCell><Badge className={img.is_active ? "status-available" : "status-expired"}>{img.is_active ? "Active" : "Inactive"}</Badge></TableCell>
                      <TableCell className="font-mono text-sm text-[#64748B]">{img.created_at ? format(new Date(img.created_at), "MMM dd, yyyy") : "-"}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button variant="ghost" size="sm" onClick={() => handleToggle(img.id)} className="text-[#002FA7]" data-testid={`toggle-image-${img.id}`}>
                            {img.is_active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(img.id)} className="text-red-500 hover:text-red-700" data-testid={`delete-image-${img.id}`}>
                            <Trash size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
